import { Menu, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { Link, useLocation } from "react-router-dom";
import mascotMark from "../../assets/mascot-mark.png";
import { useCurrentUser, useSignOut } from "../../hooks/use-auth.js";
import { useLanguage } from "../../hooks/use-language.js";
import { isAuthUnknown } from "../../lib/auth-state.js";
import { BrandWordmark } from "../ui/brand-wordmark.js";
import { Button, buttonVariants } from "../ui/button.js";
import { TierBadge } from "../ui/tier-badge.js";
import { LangToggle } from "./lang-toggle.js";
import { ThemeToggle } from "./theme-toggle.js";

export function Header() {
  const intl = useIntl();
  const { language } = useLanguage();
  const { data: user, isLoading, isPaused } = useCurrentUser();
  const signOut = useSignOut();
  const base = `/${language}`;
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: pathname is the trigger for closing the drawer on route change
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open]);

  useEffect(() => {
    if (open) closeButtonRef.current?.focus();
  }, [open]);

  function handleDrawerKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key !== "Tab") return;
    const focusable = drawerRef.current?.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    if (!focusable || focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first || !last) return;
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  const navLinkClass =
    "rounded px-2 py-1 text-sm hover:bg-muted focus-visible:outline-2 focus-visible:outline-ring";
  const drawerLinkClass =
    "rounded px-3 py-3 text-sm hover:bg-muted focus-visible:outline-2 focus-visible:outline-ring";

  return (
    // Mobile: sticky so menu + sign-in stay reachable on long pages; desktop
    // stays static (owner call 07-08). z-10 keeps floating layers (tooltips
    // z-10 by DOM order, popover z-20, menu z-40) above the bar.
    <header className="sticky top-0 z-10 border-b border-border bg-background/90 backdrop-blur md:static md:bg-background md:backdrop-blur-none">
      <nav
        className="flex h-14 items-center justify-between px-6"
        aria-label={intl.formatMessage({ id: "app.name" })}
      >
        <Link
          to={base}
          className="flex items-center gap-2 rounded font-semibold tracking-tight focus-visible:outline-2 focus-visible:outline-ring"
        >
          <img src={mascotMark} alt="" aria-hidden="true" className="h-9 w-auto" />
          <BrandWordmark />
        </Link>

        <div className="hidden md:flex items-center gap-2">
          <Link to={`${base}/docs`} className={navLinkClass}>
            {intl.formatMessage({ id: "nav.docs" })}
          </Link>
          <Link to={`${base}/features`} className={navLinkClass}>
            {intl.formatMessage({ id: "nav.features" })}
          </Link>
          <Link to={`${base}/pricing`} className={navLinkClass}>
            {intl.formatMessage({ id: "nav.pricing" })}
          </Link>
          {user && (
            <Link to={`${base}/account`} className={navLinkClass}>
              {intl.formatMessage({ id: "nav.account" })}
            </Link>
          )}
          {user?.isAdmin && (
            // Admin link is a UI hint only — server-side gates on
            // sessionUser.isAdmin (read from DB, not cookie) for every
            // /api/v1/admin/* call. Cookie tampering can't surface real data.
            <Link to="/admin" className={navLinkClass}>
              Admin
            </Link>
          )}
          {user && <TierBadge tier={user.tier} onTrial={user.trial != null} />}
          <LangToggle />
          <ThemeToggle />
          {/*
            Three states, not two - but only the third one is worth changing
            here. `user ? account : sign-in` showed "sign in" while the answer
            was in flight AND after the request failed. The first is right for
            most visitors, who really are signed out, and rendering nothing
            there would swap a rare wrong label for a guaranteed layout shift
            on every page load. The second persists, and that is the bug: a
            signed-in customer sitting there looking signed out. So: keep the
            loading behaviour, blank the slot only once the request is really
            over without an answer.

            `isPaused` belongs in that test, and its absence was a review find.
            TanStack's default networkMode is "online", so a query started
            while the browser is offline sits at fetchStatus "paused" - which
            reports isLoading FALSE with data undefined, and WILL still answer
            once connectivity returns. Without this term the slot would blank
            and then pop, which is the shift this narrowing exists to avoid.
          */}
          {isAuthUnknown(user) && !isLoading && !isPaused ? null : user ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => signOut.mutate()}
              disabled={signOut.isPending}
            >
              {intl.formatMessage({ id: "nav.sign_out" })}
            </Button>
          ) : (
            <Link to={`${base}/auth/sign-in`} className={buttonVariants({ size: "sm" })}>
              {intl.formatMessage({ id: "nav.sign_in" })}
            </Link>
          )}
        </div>

        <button
          type="button"
          className="md:hidden rounded p-1 hover:bg-muted focus-visible:outline-2 focus-visible:outline-ring"
          aria-expanded={open}
          aria-controls="mobile-nav"
          aria-label={intl.formatMessage({
            id: open ? "nav.menu_close" : "nav.menu_open",
          })}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </nav>

      {open && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-40 bg-black/40"
            aria-label={intl.formatMessage({ id: "nav.menu_close" })}
            onClick={() => setOpen(false)}
          />

          <div
            id="mobile-nav"
            ref={drawerRef}
            // biome-ignore lint/a11y/useSemanticElements: native <dialog> conflicts with the custom focus, esc, and scroll handling above
            role="dialog"
            aria-modal="true"
            aria-label={intl.formatMessage({ id: "app.name" })}
            className="fixed top-0 right-0 z-50 flex w-64 flex-col overflow-hidden rounded-bl-md border-b border-l border-border bg-background shadow-xl"
            onKeyDown={handleDrawerKeyDown}
          >
            <div className="flex h-14 items-center justify-between px-4 border-b border-border">
              <div className="flex items-center gap-2">
                <LangToggle />
                <ThemeToggle />
              </div>
              <button
                ref={closeButtonRef}
                type="button"
                className="rounded p-1 hover:bg-muted focus-visible:outline-2 focus-visible:outline-ring"
                aria-label={intl.formatMessage({ id: "nav.menu_close" })}
                onClick={() => setOpen(false)}
              >
                <X size={20} />
              </button>
            </div>

            <nav className="flex flex-col gap-1 p-3">
              <Link to={`${base}/docs`} className={drawerLinkClass}>
                {intl.formatMessage({ id: "nav.docs" })}
              </Link>
              <Link to={`${base}/features`} className={drawerLinkClass}>
                {intl.formatMessage({ id: "nav.features" })}
              </Link>
              <Link to={`${base}/pricing`} className={drawerLinkClass}>
                {intl.formatMessage({ id: "nav.pricing" })}
              </Link>
              {user && (
                <Link to={`${base}/account`} className={drawerLinkClass}>
                  {intl.formatMessage({ id: "nav.account" })}
                </Link>
              )}
              {user?.isAdmin && (
                <Link to="/admin" className={drawerLinkClass}>
                  Admin
                </Link>
              )}
            </nav>

            <div className="flex flex-col gap-3 border-t border-border p-4">
              {/* Same rule as the desktop slot above. */}
              {isAuthUnknown(user) && !isLoading && !isPaused ? null : user ? (
                <>
                  <div className="flex items-center justify-start">
                    <TierBadge tier={user.tier} onTrial={user.trial != null} />
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="w-full justify-start"
                    onClick={() => {
                      signOut.mutate();
                      setOpen(false);
                    }}
                    disabled={signOut.isPending}
                  >
                    {intl.formatMessage({ id: "nav.sign_out" })}
                  </Button>
                </>
              ) : (
                <Link to={`${base}/auth/sign-in`} className={buttonVariants({ size: "sm" })}>
                  {intl.formatMessage({ id: "nav.sign_in" })}
                </Link>
              )}
            </div>
          </div>
        </>
      )}
    </header>
  );
}
